/**
 * Client-side image crop utility. Used by profile photo/banner and collaboration thumbnail flows.
 * Returns a Blob (JPEG) from a source image and pixel crop area.
 *
 * @param {string} imageSrc - Data URL or object URL of the source image
 * @param {{ x: number, y: number, width: number, height: number }} cropPixels - Crop area in pixels (e.g. from react-easy-crop onCropComplete)
 * @param {number} [outputWidth] - Optional output width; if omitted, uses crop width
 * @param {number} [outputHeight] - Optional output height; if omitted, uses crop height
 * @returns {Promise<Blob>} JPEG blob of the cropped image
 */
export async function getCroppedImageBlob(imageSrc, cropPixels, outputWidth = null, outputHeight = null) {
  const image = new Image();
  image.src = imageSrc;
  await new Promise((resolve, reject) => {
    image.onload = resolve;
    image.onerror = () => reject(new Error('Failed to load image'));
  });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No 2d context');

  if (outputWidth != null && outputHeight != null) {
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    ctx.drawImage(
      image,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      outputWidth,
      outputHeight
    );
  } else {
    canvas.width = cropPixels.width;
    canvas.height = cropPixels.height;
    ctx.drawImage(
      image,
      cropPixels.x,
      cropPixels.y,
      cropPixels.width,
      cropPixels.height,
      0,
      0,
      cropPixels.width,
      cropPixels.height
    );
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      0.9
    );
  });
}
